import { useState, useRef, useCallback } from "react";
import posthog from "@/lib/posthog";
import { Bell } from "lucide-react";
import { ReminderDto } from "@blinkbooks/types";
import { markReminderRead } from "@/services/reminder.service";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatDistanceToNow, parseISO } from "date-fns";
import { cn } from "@/lib/utils";

interface RemindersButtonProps {
  reminders: ReminderDto[];
  onReminderRead: (id: string) => void;
}

const HOVER_READ_DELAY_MS = 5000;

function ReminderItem({ reminder, onRead }: { reminder: ReminderDto; onRead: (id: string) => void }) {
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = useCallback(() => {
    if (reminder.hasRead) return;
    hoverTimerRef.current = setTimeout(() => onRead(reminder.id), HOVER_READ_DELAY_MS);
  }, [reminder.id, reminder.hasRead, onRead]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const handleClick = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (!reminder.hasRead) onRead(reminder.id);
  }, [reminder.id, reminder.hasRead, onRead]);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
      className={cn(
        "flex flex-col gap-1 px-3 py-2.5 cursor-pointer select-none border-b last:border-b-0 transition-colors",
        reminder.hasRead
          ? "bg-background hover:bg-muted/30 opacity-60"
          : "bg-blue-50/50 hover:bg-blue-50 dark:bg-blue-950/20 dark:hover:bg-blue-950/40"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className={cn("text-xs font-medium", !reminder.hasRead && "font-semibold")}>
          {reminder.text}
        </span>
        {!reminder.hasRead && (
          <span className="mt-0.5 h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />
        )}
      </div>
      <span className="text-xs text-muted-foreground">
        {formatDistanceToNow(parseISO(reminder.createdAt), { addSuffix: true })}
      </span>
    </div>
  );
}

export function RemindersButton({ reminders, onReminderRead }: RemindersButtonProps) {
  const [open, setOpen] = useState(false);
  const unreadCount = reminders.filter(r => !r.hasRead).length;

  const handleRead = useCallback(async (id: string) => {
    onReminderRead(id);
    posthog.capture("reminder_read", { reminder_id: id });
    try {
      await markReminderRead(id);
    } catch {
      // optimistic update already applied
    }
  }, [onReminderRead]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) posthog.capture("reminders_opened", { unread_count: unreadCount });
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className="relative h-8 w-8 flex items-center justify-center rounded-md hover:bg-muted transition-colors"
          title="Reminders"
          aria-label={`Reminders${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        >
          <Bell className="h-4 w-4 md:h-5 md:w-5 text-foreground" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-blue-500 text-[10px] font-bold text-white flex items-center justify-center leading-none">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" sideOffset={8}>
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="text-sm font-semibold">Reminders</span>
          {unreadCount > 0 && (
            <span className="text-xs text-muted-foreground">{unreadCount} unread</span>
          )}
        </div>
        {reminders.length === 0 ? (
          <div className="px-3 py-8 text-center text-sm text-muted-foreground">No reminders</div>
        ) : (
          <ScrollArea className="max-h-[360px]">
            {reminders.map(r => (
              <ReminderItem key={r.id} reminder={r} onRead={handleRead} />
            ))}
          </ScrollArea>
        )}
      </PopoverContent>
    </Popover>
  );
}
